import React, { useState, useEffect } from 'react'
import { ExplorePlans } from '../ExplorePlansMenu'
import {
  careerAdviceEventHandle,
  loginClickEvent,
  signUpClickEvent,
} from '../HeaderClickEvents/Events'
import { ServiceMenu } from '../ServiceMenu'
import { Link } from '@remix-run/react'
import { MainLogoIcon } from '~/components/icons/logos'
import classNames from 'classnames'
import { Button } from '~/components/general/Button'
import { TypeNavigationLink } from '~/types/contentful'
import { Asset, Entry, EntrySkeletonType } from 'contentful'

export type NavItem = {
  title: string
  link: string
  description?: string | undefined
  icon?: Asset<'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined
  isAuthRequired?: boolean | undefined
  label?: string | undefined
  color?: string | undefined
  groups?:
    | (Entry<EntrySkeletonType, 'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined)[]
    | undefined
}

type DesktopHeaderProps = {
  navItems: TypeNavigationLink<'WITHOUT_UNRESOLVABLE_LINKS', string>[]
  userAuthorized: boolean
  isDarkBg?: boolean
  isFixed?: boolean
  createAccountLink: string
  setDropDownMenuActive: (value: boolean) => void
  isLoading: boolean
}

const DesktopHeader = ({
  navItems,
  isDarkBg,
  isFixed,
  userAuthorized,
  createAccountLink,
  setDropDownMenuActive,
  isLoading,
}: DesktopHeaderProps) => {
  const [isServiceMenuActive, setIsServiceMenuActive] = useState<boolean>(false)
  const [isExplorePlansMenuActive, setIsExplorePlansMenuActive] = useState<boolean>(false)

  useEffect(() => {
    if (isServiceMenuActive || isExplorePlansMenuActive) setDropDownMenuActive(true)
    else setDropDownMenuActive(false)
  }, [isServiceMenuActive, isExplorePlansMenuActive, setDropDownMenuActive])

  const checkIfDarkBg = !isFixed && isDarkBg && !isServiceMenuActive && !isExplorePlansMenuActive

  return (
    <div className="flex items-center justify-between h-full max-xl:hidden">
      <Link className="py-2 lg:w-64 md:w-36 w-32" to="/">
        <MainLogoIcon
          className={classNames('w-full max-w-40', checkIfDarkBg ? 'text-white' : 'text-black')}
        />
      </Link>

      <div className="flex items-center flex-grow justify-center gap-2 text-xs max-xl:hidden">
        {navItems?.map((item, idx: number) => {
          const nav = item.fields

          switch (nav?.title) {
            case 'Services':
              return (
                <ServiceMenu
                  key={`${nav?.title + idx}}`}
                  setDropdown={setIsServiceMenuActive}
                  isActive={isServiceMenuActive}
                  userAuthorized={userAuthorized}
                  menuData={nav as NavItem}
                  checkIfDarkBg={checkIfDarkBg}
                />
              )
            case 'Explore Plans':
              return (
                <ExplorePlans
                  key={`${nav?.title + idx}}`}
                  setDropdown={setIsExplorePlansMenuActive}
                  isActive={isExplorePlansMenuActive}
                  userAuthorized={userAuthorized}
                  menuData={nav as NavItem}
                  checkIfDarkBg={checkIfDarkBg}
                />
              )
            default:
              return (
                <React.Fragment key={`${nav.link}_${idx}`}>
                  <Button
                    type={'link'}
                    target={'_self'}
                    text={nav.title}
                    href={nav.link}
                    color={'transparent'}
                    onClick={careerAdviceEventHandle}
                    className={classNames(
                      'w-fit !text-mobile-s',
                      checkIfDarkBg ? '!text-white hover:!text-neutral-100' : '!text-neutral-100',
                    )}
                  />
                </React.Fragment>
              )
          }
        })}
      </div>

      <div
        className="w-[250px] transition-opacity duration-200 gap-2 text-xs max-xl:hidden max-xl:w-auto max-xl:gap-1 flex items-center"
        style={{ visibility: isLoading ? 'hidden' : 'visible' }}
      >
        {userAuthorized ? (
          <Button
            text={'My Dashboard'}
            href={'/app'}
            className={'w-fit !text-mobile-s ml-10'}
            type={'link'}
            target={'_self'}
          />
        ) : (
          <>
            <Button
              text={'Log In'}
              href={'/app/auth/sign-in'}
              onClick={loginClickEvent}
              className={classNames(
                'w-fit !text-mobile-s',
                checkIfDarkBg ? '!text-white hover:!text-neutral-100' : '!text-neutral-100',
              )}
              type={'link'}
              color={'transparent'}
              target={'_self'}
            />
            <Button
              text={'Sign Up'}
              href={createAccountLink}
              onClick={signUpClickEvent}
              className={'w-fit !text-mobile-s'}
              type={'link'}
              target={'_self'}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default DesktopHeader
